import React, { useEffect, useMemo, useState } from "react";
import { httpGet, httpPost, httpPatch, httpDelete } from "../../components/helpers/httpMethods";
import "./account.css";
import { Settings, Gifts } from "./AccountMore";
import { Applications } from "./AccountApps";
import { SettingsHub, ProfileNudge, CompanyNews, BirthdayPanel } from "./AccountProfile";
import { Bills } from "./AccountBills";
import { BillsReward } from "./BillsReward";
import { useBillsOn } from "../../components/Bills/billsOn";
import "../../components/Bills/billsTouch.css";
import BannerSlot from "../../components/Banner/BannerSlot";
import { loadFollowing, toggleFollow } from "../../components/Follow/follow";

// Account hub: Overview, My library, Subscriptions, Payments & receipts. Gifts and settings link to the existing pages for now.
const LINKS = { subscriptions: "/account/subscriptions", payments: "/account/payments", gifts: "/account/gifts", settings: "/account/settings" };
const NAV = [
  ["overview", "Overview", "/account", "◧"],
  ["library", "My library", "/account/library", "▤"],
  ["subscriptions", "Subscriptions", LINKS.subscriptions, "↻"],
  ["payments", "Payments & receipts", LINKS.payments, "₦"],
  ["applications", "My applications", "/account/applications", "📄"],
  ["gifts", "Gifts for others", LINKS.gifts, "🎁"],
  ["bills", "Pay bills", "/account/bills", "₦"],
  ["settings", "Account settings", LINKS.settings, "⚙"],
];
const NAV_ICONS = {
  applications: <><path d="M6 3h9l4 4v14H6z" /><path d="M14 3v5h5M9 13h7M9 17h5" /></>,
  overview: <><path d="M4 4h7v7H4zM13 4h7v4h-7zM13 10h7v10h-7zM4 13h7v7H4z" /></>,
  library: <path d="M4 4h6v16H4zM14 4h6v16h-6z" />,
  subscriptions: <><path d="M4 7h16v12H4z" /><path d="M4 11h16M8 3v4M16 3v4" /></>,
  payments: <><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M3 10h18M7 15h3" /></>,
  gifts: <><rect x="3" y="9" width="18" height="12" rx="1" /><path d="M3 13h18M12 9v12M12 9c-2 0-4-1-4-3s2-2 4 1c2-3 4-3 4-1s-2 3-4 3z" /></>,
  bills: <path d="M6 2h12v20l-3-2-3 2-3-2-3 2zM9 7h6M9 11h6M9 15h4" />,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M19 12a7 7 0 0 0-.1-1.2l2-1.6-2-3.4-2.4.9a7 7 0 0 0-2-1.2L14 3h-4l-.5 2.5a7 7 0 0 0-2 1.2l-2.4-.9-2 3.4 2 1.6A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.6 2 3.4 2.4-.9a7 7 0 0 0 2 1.2L10 21h4l.5-2.5a7 7 0 0 0 2-1.2l2.4.9 2-3.4-2-1.6c.1-.4.1-.8.1-1.2z" /></>,
};
const naira = (v) => `₦${Math.round(Number(v) || 0).toLocaleString("en-NG")}`;
const day = (d, o = { weekday: "short", day: "numeric", month: "short", year: "numeric" }) => {
  const x = d ? new Date(d) : null;
  return x && !Number.isNaN(x.getTime()) ? x.toLocaleDateString("en-GB", { ...o, timeZone: "Africa/Lagos" }) : "";
};
const isToday = (d) => day(d) === day(new Date());
const titleCase = (s) => (s && s === s.toUpperCase() ? s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : s || "");
const PLAN = { "one-off": "Single edition", weekly: "Weekly", monthly: "Monthly", quarterly: "Every 3 months", biannually: "Every 6 months", annually: "Yearly" };
const lastPage = (editionId) => { try { return Number(JSON.parse(localStorage.getItem(`tns-reader-last:${editionId}`))) || 0; } catch (e) { return 0; } };
const payText = (p) => {
  const paper = titleCase(p.paper);
  if (p.message === "Archive purchase") return `Archive · ${paper}${p.editionDate ? `, ${day(p.editionDate)}` : ""}`;
  if (p.message === "Subscription payment") return `Subscription · ${paper}`;
  if (p.message === "CON Payment") return "Change of name";
  if (p.bill) return p.bill;
  return p.message || paper || "Payment";
};

function Cover({ src, big }) {
  return <span className={`ac-cover${big ? " big" : ""}`}>{src ? <img src={src} alt="" loading="lazy" /> : null}</span>;
}

function Overview({ data, history }) {
  const { user, subscriptions: subs, library, payments } = data;
  const billsOn = useBillsOn();
  const next = subs.find((s) => s.nextPaymentDate && !s.isCancelled && Number(s.amount) > 0);
  const resume = [
    ...subs.filter((s) => s.latest).map((s) => ({ key: `s${s.id}`, id: s.latest.id, image: s.latest.image, paper: titleCase(s.name),
      line: `${isToday(s.latest.publishedDate) ? "Today" : day(s.latest.publishedDate)}`, action: lastPage(s.latest.id) > 1 ? `Continue from page ${lastPage(s.latest.id)} ›` : "Read ›" })),
    ...library.map((b) => ({ key: `b${b.id}`, id: b.editionId, image: b.image, paper: titleCase(b.publication), line: `${day(b.publishedDate)} · in your library`,
      action: lastPage(b.editionId) > 1 ? `Continue from page ${lastPage(b.editionId)} ›` : "Read ›" })),
  ].slice(0, 6);
  return (
    <>
      <div className="ac-title"><h1>Hello, {user && user.firstName ? user.firstName : "there"}</h1><p>Your papers, library and account in one place.</p></div>
      <ProfileNudge history={history} />
      <BirthdayPanel history={history} user={user} />
      <CompanyNews history={history} />
      <div className="ac-stats">
        <div className="ac-card ac-stat"><span>Active subscriptions</span><strong>{subs.length}</strong><small>{subs.length ? subs.slice(0, 3).map((s) => titleCase(s.name)).join(", ") : "None yet"}</small></div>
        <div className="ac-card ac-stat"><span>In your library</span><strong>{library.length} edition{library.length === 1 ? "" : "s"}</strong><small>Bought from the archive</small></div>
        <div className="ac-card ac-stat"><span>Next renewal</span><strong>{next ? day(next.nextPaymentDate, { weekday: "short", day: "numeric", month: "short" }) : "—"}</strong>
          <small>{next ? `${titleCase(next.name)} · ${PLAN[next.plan] || next.plan} · ${naira(next.amount)}` : "No automatic renewals"}</small></div>
      </div>
      <section className="ac-card">
        <div className="ac-head"><h2>Pick up where you left off</h2><button type="button" className="ac-link" onClick={() => history.push("/account/library")}>My library ›</button></div>
        {resume.length ? (
          <div className="ac-resume">
            {resume.map((r) => (
              <button type="button" key={r.key} className="ac-resume-item" onClick={() => history.push(`/view_single_paper/${r.id}`)}>
                <Cover src={r.image} />
                <span className="ac-resume-text"><strong>{r.paper}</strong><small>{r.line}</small><em>{r.action}</em></span>
              </button>
            ))}
          </div>
        ) : (
          <div className="ac-empty"><p>Subscribe to a paper and it will appear here every morning.</p><button type="button" className="ac-btn" onClick={() => history.push("/")}>Browse papers</button></div>
        )}
      </section>
      <section className="ac-card ac-offer">
        <span className="ac-offer-icon" aria-hidden="true">🗂️</span>
        <span className="ac-offer-text"><strong>Old issues, now in TheNewspaperStand’s archive</strong><small>{subs.length ? "As a subscriber you pay 30% less for any past edition." : "Find any past edition and keep it for good."}</small></span>
        <button type="button" className="ac-btn" onClick={() => history.push("/archive")}>Browse the archive</button>
      </section>
      {billsOn ? <BillsReward history={history} compact /> : null}
      {billsOn ? (
        <section className="ac-card ac-offer bt-quiet">
          <span className="ac-offer-icon bt-quiet-icon" aria-hidden="true"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" /></svg></span>
          <span className="ac-offer-text"><strong>Pay a bill</strong><small>Airtime, data, electricity and TV, delivered in seconds.</small></span>
          <button type="button" className="ac-btn2" onClick={() => history.push("/account/bills")}>Pay a bill</button>
        </section>
      ) : null}
      <section className="ac-card">
        <div className="ac-head"><h2>Recent payments</h2><button type="button" className="ac-link" onClick={() => history.push(LINKS.payments)}>All payments ›</button></div>
        {payments.length ? payments.map((p) => (
          <div className="ac-pay" key={p.reference}>
            <span><strong>{payText(p)}</strong><small>{day(p.createdAt)}</small></span>
            <span className="ac-pay-right"><strong>{p.currency === "USD" ? `$${p.amount}` : naira(p.amount)}</strong><span className="ac-pill ok">Paid</span></span>
          </div>
        )) : <p className="ac-muted">No payments yet.</p>}
      </section>
    </>
  );
}

const saveKey = (s) => `${s.editionId}:${s.pageNumber}`;

// Saved pages: pages kept from the reader (Bookmarks), each with an optional private note.
// Grouped by edition (one cover, pages in page order), newest edition first.
function SavedPages({ saves, setSaves, q, paper, history }) {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [undo, setUndo] = useState(null);
  useEffect(() => {
    if (!undo) return undefined;
    const t = setTimeout(() => setUndo(null), 6000);
    return () => clearTimeout(t);
  }, [undo]);
  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const m = new Map();
    (saves || []).forEach((s) => {
      if (paper && String(s.publicationId) !== paper) return;
      if (needle && !String(s.note || "").toLowerCase().includes(needle) && !String(s.paper || "").toLowerCase().includes(needle)) return;
      if (!m.has(s.editionId)) m.set(s.editionId, { ...s, pages: [] });
      m.get(s.editionId).pages.push(s);
    });
    const list = [...m.values()];
    list.forEach((g) => g.pages.sort((a, b) => a.pageNumber - b.pageNumber));
    return list.sort((a, b) => new Date(b.publishedDate) - new Date(a.publishedDate));
  }, [saves, q, paper]);
  if (saves === null) return <div className="ac-card"><p className="ac-muted">Loading…</p></div>;
  const open = (s) => history.push(`/view_single_paper/${s.editionId}?page=${s.pageNumber}`);
  const edit = (s) => { setEditing(saveKey(s)); setDraft(s.note || ""); };
  const saveNote = (e, s) => {
    e.preventDefault();
    const note = draft.trim().slice(0, 280);
    setSaves((list) => list.map((x) => (saveKey(x) === saveKey(s) ? { ...x, note: note || null } : x)));
    setEditing(null);
    httpPatch(`me/saves/${s.editionId}/${s.pageNumber}`, { note });
  };
  const remove = (s) => {
    setSaves((list) => list.filter((x) => saveKey(x) !== saveKey(s)));
    setUndo(s);
    httpDelete(`me/saves/${s.editionId}/${s.pageNumber}`);
  };
  const restore = async () => {
    const s = undo;
    setUndo(null);
    setSaves((list) => [...list, s].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    await httpPost("me/saves", { editionId: s.editionId, pageNumber: s.pageNumber });
    if (s.note) httpPatch(`me/saves/${s.editionId}/${s.pageNumber}`, { note: s.note });
  };
  return (
    <>
      {!saves.length ? (
        <div className="ac-card ac-empty"><p>No saved pages yet. While reading, tap the bookmark on any page to keep it here, with a note if you like.</p><button type="button" className="ac-btn" onClick={() => history.push("/")}>Find something to read</button></div>
      ) : !groups.length ? (
        <div className="ac-card"><p className="ac-muted">No saved pages match your search.</p></div>
      ) : (
        <div className="ac-saves">
          {groups.map((g) => (
            <section className="ac-card ac-save-ed" key={g.editionId}>
              <button type="button" className="ac-save-cover" onClick={() => open(g.pages[0])} aria-label={`Open ${titleCase(g.paper)}, ${day(g.publishedDate)}`}><Cover src={g.image} big /></button>
              <div className="ac-save-body">
                <div className="ac-save-head"><strong>{titleCase(g.paper)}</strong><small>{day(g.publishedDate)} · {g.pages.length} saved page{g.pages.length === 1 ? "" : "s"}</small></div>
                <ul className="ac-save-list">
                  {g.pages.map((s) => (
                    <li className="ac-save-row" key={saveKey(s)}>
                      <button type="button" className="ac-save-pg" onClick={() => open(s)} aria-label={`Open page ${s.pageNumber}`}>p.{s.pageNumber}</button>
                      {editing === saveKey(s) ? (
                        <form className="ac-save-form" onSubmit={(e) => saveNote(e, s)}>
                          <textarea className="ac-input ac-save-input" value={draft} maxLength={280} autoFocus aria-label="Your note" placeholder="Why did you save this page?" onChange={(e) => setDraft(e.target.value)} />
                          <span className="ac-save-actions"><button type="submit" className="ac-link">Save note</button><button type="button" className="ac-link ac-quiet" onClick={() => setEditing(null)}>Cancel</button></span>
                        </form>
                      ) : s.note ? (
                        <button type="button" className="ac-save-note" onClick={() => edit(s)} title="Edit note">{s.note}</button>
                      ) : (
                        <button type="button" className="ac-link ac-quiet ac-save-add" onClick={() => edit(s)}>+ Add a note</button>
                      )}
                      {editing === saveKey(s) ? null : (
                        <span className="ac-save-actions"><button type="button" className="ac-link" onClick={() => open(s)}>Open ›</button><button type="button" className="ac-link ac-quiet" onClick={() => remove(s)}>Remove</button></span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          ))}
        </div>
      )}
      {undo ? (
        <div className="ac-undo" role="status">Page {undo.pageNumber} removed<button type="button" onClick={restore}>Undo</button></div>
      ) : null}
    </>
  );
}

// My library → Following: papers the reader follows, newest edition first. Unfollow keeps the row (with Follow again) until they leave.
const slugOf = (name) => String(name || "").trim().replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase();
function Following({ list, history }) {
  const [off, setOff] = useState(() => new Set());
  if (list === null) return <div className="ac-card"><p className="ac-muted">Loading…</p></div>;
  if (!list.length) {
    return (
      <div className="ac-card ac-empty">
        <p>You aren’t following any papers yet. Tap the follow icon on a paper and it shows first on your home page and here.</p>
        <button type="button" className="ac-btn" onClick={() => history.push("/home")}>Find papers</button>
      </div>
    );
  }
  const flip = async (p) => {
    const nowOn = await toggleFollow(p.id, titleCase(p.name));
    setOff((o) => { const n = new Set(o); if (nowOn) n.delete(p.id); else n.add(p.id); return n; });
  };
  return (
    <div className="ac-grid">
      {list.map((p) => (
        <div className={`ac-card ac-item ${off.has(p.id) ? "ac-faded" : ""}`} key={p.id}>
          <Cover src={p.cover || p.logo} big />
          <strong>{titleCase(p.name)}</strong>
          <small>{p.publishedDate ? `Latest: ${day(p.publishedDate)}` : "No editions yet"}</small>
          {p.status !== "active" ? <small>Not publishing on TheNewspaperStand right now</small> : null}
          <button type="button" className="ac-btn" onClick={() => history.push(`/publication/${slugOf(p.name)}`)}>Open paper</button>
          <button type="button" className="ac-link" onClick={() => flip(p)}>{off.has(p.id) ? "Follow again" : "Unfollow"}</button>
        </div>
      ))}
    </div>
  );
}

function Library({ data, history }) {
  const [tab, setTabState] = useState(() => ({ saved: "saved", following: "following" }[new URLSearchParams(window.location.search).get("tab")] || "bought"));
  const [following, setFollowing] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("bought");
  const [requests, setRequests] = useState(null);
  const [saves, setSaves] = useState(null);
  const [sq, setSq] = useState("");
  const [paper, setPaper] = useState("");
  const setTab = (t) => { setTabState(t); history.replace(t === "saved" || t === "following" ? `/account/library?tab=${t}` : "/account/library"); };
  useEffect(() => {
    httpGet("archive_requests/mine").then((r) => {
      const d = r && r.data;
      setRequests(Array.isArray(d) ? d : d && (d.requests || d.items || []) ? (d.requests || d.items || []) : []);
    });
    httpGet("me/saves").then((r) => setSaves(r && r.data && Array.isArray(r.data.saves) ? r.data.saves : []));
    httpGet("me/following").then((r) => setFollowing(r && r.data && Array.isArray(r.data.papers) ? r.data.papers : []));
    loadFollowing();
  }, []);
  const papers = useMemo(() => {
    const m = new Map();
    (saves || []).forEach((s) => m.set(String(s.publicationId), titleCase(s.paper)));
    return [...m].sort((a, b) => a[1].localeCompare(b[1]));
  }, [saves]);
  const items = useMemo(() => {
    const list = data.library.filter((b) => !q.trim() || String(b.publication).toLowerCase().includes(q.trim().toLowerCase()));
    return [...list].sort((a, b) => (sort === "edition" ? new Date(b.publishedDate) - new Date(a.publishedDate) : new Date(b.boughtAt) - new Date(a.boughtAt)));
  }, [data.library, q, sort]);
  const reqs = requests || [];
  return (
    <>
      <div className="ac-title"><h1>My library</h1><p>{tab === "saved" ? "Pages you’ve saved while reading, with your notes. Only you can see them." : tab === "following" ? "Papers you follow show first on your home page, and we email you once a week when they have new editions." : "Editions you’ve bought are yours to keep. Read them any time, on any device."}</p></div>
      <BannerSlot spot="library_top" inline />
      <div className="ac-bar">
        <div className="ac-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "bought"} className={tab === "bought" ? "on" : ""} onClick={() => setTab("bought")}>Bought ({data.library.length})</button>
          <button type="button" role="tab" aria-selected={tab === "requested"} className={tab === "requested" ? "on" : ""} onClick={() => setTab("requested")}>Requested ({requests ? reqs.length : "…"})</button>
          <button type="button" role="tab" aria-selected={tab === "saved"} className={tab === "saved" ? "on" : ""} onClick={() => setTab("saved")}>Saved pages ({saves ? saves.length : "…"})</button>
          <button type="button" role="tab" aria-selected={tab === "following"} className={tab === "following" ? "on" : ""} onClick={() => setTab("following")}>Following ({following ? following.length : "…"})</button>
        </div>
        <span className="ac-grow" />
        {tab === "saved" && saves && saves.length ? (
          <>
            <input className="ac-input" type="search" placeholder="Search your notes" value={sq} onChange={(e) => setSq(e.target.value)} aria-label="Search your notes" />
            {papers.length > 1 ? (
              <select className="ac-input ac-select" value={paper} onChange={(e) => setPaper(e.target.value)} aria-label="Filter by paper">
                <option value="">All papers</option>
                {papers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            ) : null}
          </>
        ) : tab === "bought" ? (
          <>
            <input className="ac-input" type="search" placeholder="Search your library" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search your library" />
            <select className="ac-input ac-select" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort">
              <option value="bought">Recently bought</option>
              <option value="edition">Newest edition</option>
            </select>
          </>
        ) : null}
      </div>
      {tab === "following" ? (
        <Following list={following} history={history} />
      ) : tab === "saved" ? (
        <SavedPages saves={saves} setSaves={setSaves} q={sq} paper={paper} history={history} />
      ) : tab === "bought" ? (
        <div className="ac-grid">
          {items.map((b) => {
            const lp = lastPage(b.editionId);
            return (
              <div className="ac-card ac-item" key={b.id}>
                <Cover src={b.image} big />
                <strong>{titleCase(b.publication)}</strong>
                <small>{day(b.publishedDate)}</small>
                <small>Bought {day(b.boughtAt, { day: "numeric", month: "short", year: "numeric" })}{lp > 1 ? ` · page ${lp}` : ""}</small>
                <button type="button" className="ac-btn" onClick={() => history.push(`/view_single_paper/${b.editionId}`)}>{lp > 1 ? "Continue reading" : "Read"}</button>
              </div>
            );
          })}
          <button type="button" className="ac-find" onClick={() => history.push("/archive")}>
            <span aria-hidden="true">🗂️</span>{data.library.length ? "Looking for another past issue?" : "Nothing here yet. Find any past edition and keep it for good."}<em>Browse the archive ›</em>
          </button>
        </div>
      ) : (
        <div className="ac-card">
          {requests === null ? <p className="ac-muted">Loading…</p> : !reqs.length ? (
            <div className="ac-empty"><p>You haven’t requested any issues. If an edition is missing from the archive, request it free and we’ll email you when it’s ready.</p><button type="button" className="ac-btn" onClick={() => history.push("/archive")}>Go to the archive</button></div>
          ) : reqs.map((r, i) => (
            <div className="ac-pay" key={r.id || i}>
              <span><strong>{titleCase(r.publication || (r.Publication && r.Publication.name) || r.name || "Requested issue")}</strong><small>{day(r.date || r.editionDate || r.day)}</small></span>
              <span className="ac-pay-right"><span className={`ac-pill ${/ready|done|fulfil/i.test(r.status) ? "ok" : ""}`}>{/ready|done|fulfil/i.test(r.status) ? "Ready" : "Requested"}</span></span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

const money = (p) => (p.currency === "USD" ? `$${Number(p.value || p.amount || 0).toFixed(2)}` : naira(p.value || p.amount));
const how = (b) => (!b ? "—" : /card/i.test(b) ? "Card" : /transfer|bank/i.test(b) ? "Bank transfer" : /ussd/i.test(b) ? "USSD" : titleCase(String(b)));
const paperLink = (name) => `/publication/${encodeURIComponent(String(name || "").trim())}`;

function Subscriptions({ history }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("active");
  useEffect(() => {
    httpGet("me/subscriptions").then((r) => setD(r && r.data && Array.isArray(r.data.active) ? r.data : { active: [], ended: [] }));
  }, []);
  const list = d ? d[tab] : [];
  return (
    <>
      <div className="ac-title"><h1>Subscriptions</h1><p>Every new edition of the papers you subscribe to, plus the last 30 days.</p></div>
      <div className="ac-bar">
        <div className="ac-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "active"} className={tab === "active" ? "on" : ""} onClick={() => setTab("active")}>Active ({d ? d.active.length : "…"})</button>
          <button type="button" role="tab" aria-selected={tab === "ended"} className={tab === "ended" ? "on" : ""} onClick={() => setTab("ended")}>Ended ({d ? d.ended.length : "…"})</button>
        </div>
      </div>
      {!d ? <p className="ac-muted">Loading your subscriptions…</p> : !list.length ? (
        <div className="ac-card ac-empty">
          <p>{tab === "active" ? "You don’t have an active subscription. Subscribe to a paper to get every new edition as soon as it’s published." : "No ended subscriptions."}</p>
          {tab === "active" ? <button type="button" className="ac-btn" onClick={() => history.push("/")}>Browse papers</button> : null}
        </div>
      ) : list.map((s) => {
        const free = !Number(s.amount);
        const status = tab === "ended" ? ["Ended", ""] : free ? ["Free", "info"] : s.isCancelled ? ["Renewal off", "warn"] : ["Active", "ok"];
        const line = tab === "ended"
          ? `${s.nextPaymentDate ? `Ended ${day(s.nextPaymentDate)}` : "Ended"}`
          : !s.nextPaymentDate ? "No end date"
            : s.isCancelled || free ? `Ends on ${day(s.nextPaymentDate)}${s.isCancelled ? " · automatic renewal is off" : ""}`
              : `Renews automatically on ${day(s.nextPaymentDate)}`;
        return (
          <div className="ac-card ac-sub" key={s.id}>
            <Cover src={s.logo || s.image} />
            <span className="ac-sub-text">
              <span className="ac-sub-name"><strong>{titleCase(s.name)}</strong><span className={`ac-pill ${status[1]}`}>{status[0]}</span></span>
              <small>{PLAN[s.plan] || titleCase(s.plan || "")}{free ? "" : ` · ${s.currency === "USD" ? `$${s.amount}` : naira(s.amount)}`}{s.isBuyForMe ? " · bought for someone" : ""}</small>
              <span className="ac-sub-line">{line}</span>
            </span>
            <span className="ac-sub-actions">
              {tab === "active" && s.latest ? <button type="button" className="ac-btn" onClick={() => history.push(`/view_single_paper/${s.latest.id}`)}>Read latest</button> : null}
              <button type="button" className="ac-btn2" onClick={() => history.push(paperLink(s.name))}>{tab === "active" ? "Change plan" : "Subscribe again"}</button>
            </span>
          </div>
        );
      })}
      {d && d.active.length ? <p className="ac-muted">To turn automatic renewal on or off, use <button type="button" className="ac-link" onClick={() => { window.location.href = "/user/subscription"; }}>Manage renewals ›</button></p> : null}
    </>
  );
}

function receipt(p, user) {
  const w = window.open("", "_blank", "width=520,height=720");
  if (!w) return;
  const who = user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "";
  const esc = (v) => String(v || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  w.document.write(`<!doctype html><html><head><title>TNS receipt ${esc(p.reference)}</title><style>
    body{font-family:Arial,Helvetica,sans-serif;color:#14142b;padding:32px;max-width:460px;margin:auto}h1{font-size:20px;margin:0 0 4px}
    .muted{color:#4e4b66;font-size:13px}table{width:100%;border-collapse:collapse;margin:20px 0}td{padding:8px 0;border-bottom:1px solid #eee;font-size:14px}
    td:last-child{text-align:right;font-weight:bold}.total td{font-size:17px;border-bottom:0}.paid{display:inline-block;padding:3px 10px;border-radius:12px;background:#e8f5ee;color:#1f7a4d;font-size:12px;font-weight:bold}
    @media print{button{display:none}}</style></head><body>
    <h1>TheNewspaperStand</h1><div class="muted">Receipt</div>
    <table><tr><td>Date</td><td>${esc(day(p.createdAt))}</td></tr><tr><td>Customer</td><td>${esc(who)}<br><span class="muted">${esc(user && user.email)}</span></td></tr>
    <tr><td>Item</td><td>${esc(payText(p))}</td></tr><tr><td>Paid by</td><td>${esc(how(p.bank))}</td></tr><tr><td>Reference</td><td>${esc(p.reference)}</td></tr>
    <tr class="total"><td>Total</td><td>${esc(money(p))}</td></tr></table><span class="paid">PAID</span>
    <p class="muted">Thank you for reading with TNS.</p><button onclick="window.print()">Print or save as PDF</button></body></html>`);
  w.document.close();
}

function Payments({ user }) {
  const [free, setFree] = useState(false);
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("all");
  useEffect(() => {
    setD(null);
    httpGet(`me/payments${free ? "?free=1" : ""}`).then((r) => setD(r && r.data && Array.isArray(r.data.payments) ? r.data.payments : []));
  }, [free]);
  const rows = (d || []).filter((p) => tab === "all" || p.state === tab);
  const csv = () => {
    const lines = [["Date", "What", "How", "Amount", "Status", "Reference"], ...rows.map((p) => [day(p.createdAt), payText(p), how(p.bank), money(p), p.state === "paid" ? "Paid" : p.state === "free" ? "Free" : p.state === "refunded" ? "Refunded" : "Not completed", p.reference])];
    const blob = new Blob([lines.map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "tns-payments.csv";
    a.click();
  };
  const PILL = { paid: ["Paid", "ok"], free: ["Free", "info"], refunded: ["Refunded", "info"], not_completed: ["Not completed", ""] };
  return (
    <>
      <div className="ac-title"><h1>Payments &amp; receipts</h1><p>Everything you’ve paid for on TNS, with receipts you can download.</p></div>
      <section className="ac-card">
        <div className="ac-bar" style={{ marginBottom: 12 }}>
          <div className="ac-tabs" role="tablist">
            {[["all", "All"], ["paid", "Paid"], ["not_completed", "Not completed"]].map(([k, l]) => <button type="button" key={k} role="tab" aria-selected={tab === k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>{l}</button>)}
          </div>
          <span className="ac-grow" />
          <label className="ac-check"><input type="checkbox" checked={free} onChange={(e) => setFree(e.target.checked)} /> Show free items</label>
          <button type="button" className="ac-btn2" onClick={csv} disabled={!rows.length}>Download (CSV)</button>
        </div>
        {!d ? <p className="ac-muted">Loading your payments…</p> : !rows.length ? <p className="ac-muted">No payments here.</p> : (
          <div className="ac-table-wrap">
            <table className="ac-table">
              <thead><tr><th>Date</th><th>What</th><th>How</th><th>Amount</th><th>Status</th><th><span className="ac-sr">Receipt</span></th></tr></thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.reference}>
                    <td data-label="Date">{day(p.createdAt)}</td>
                    <td data-label="What"><strong>{payText(p)}</strong></td>
                    <td data-label="How">{how(p.bank)}</td>
                    <td data-label="Amount"><strong>{p.state === "free" ? "Free" : money(p)}</strong></td>
                    <td data-label="Status"><span className={`ac-pill ${PILL[p.state][1]}`}>{PILL[p.state][0]}</span></td>
                    <td>{p.state === "paid" ? <button type="button" className="ac-link" onClick={() => receipt(p, user)}>Receipt</button> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="ac-muted" style={{ marginTop: 12 }}>Something wrong with a payment? Email support and quote the payment reference.</p>
      </section>
    </>
  );
}

export default function Account({ history, location }) {
  const path = (location && location.pathname) || window.location.pathname;
  const section = /library/.test(path) ? "library" : /subscriptions/.test(path) ? "subscriptions" : /payments/.test(path) ? "payments" : /settings/.test(path) ? "settings" : /gifts/.test(path) ? "gifts" : /applications/.test(path) ? "applications" : /bills/.test(path) ? "bills" : "overview";
  const [data, setData] = useState(null);
  const billsOn = useBillsOn(); // menu item only once Pay bills is switched on for this reader
  const [error, setError] = useState("");
  useEffect(() => {
    httpGet("me/overview").then((r) => {
      if (r && r.data && Array.isArray(r.data.subscriptions)) { setData(r.data); return; }
      const msg = (r && r.er && (r.er.message || String(r.er))) || "We couldn’t load your account.";
      setError(/log ?in|token|unauthori|expired/i.test(msg) ? "login" : msg);
    });
  }, []);
  const go = (to) => (to.startsWith("/account") ? history.push(to) : (window.location.href = to));
  const user = data && data.user;
  return (
    <div className="ac-page">
      <div className="ac-layout">
        <aside className="ac-side">
          <div className="ac-card ac-me">
            <span className="ac-avatar">{user && user.avatar ? <img src={user.avatar} alt="" /> : (user && user.firstName ? user.firstName[0] : "")}</span>
            <span className="ac-me-text"><strong>{user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "Your account"}</strong><small>{user ? user.email : ""}</small></span>
          </div>
          <nav className="ac-card ac-nav" aria-label="Account">
            {NAV.filter(([key]) => key !== "bills" || billsOn || section === "bills").map(([key, label, to, icon]) => (
              <button type="button" key={key} className={section === key ? "on" : ""} aria-current={section === key ? "page" : undefined} onClick={() => go(to)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{NAV_ICONS[key]}</svg>{label}{key === "library" && data ? <em>{data.library.length}</em> : null}
              </button>
            ))}
          </nav>
          <div className="ac-pub"><strong>Are you a publisher?</strong><small>Sell your paper, magazine or book on TheNewspaperStand.</small><a href="/publish">Start publishing ›</a></div>
        </aside>
        <main className="ac-main">
          {error === "login" ? (
            <div className="ac-card ac-empty"><p>Please log in to see your account.</p><button type="button" className="ac-btn" onClick={() => history.push("/login")}>Log in</button></div>
          ) : error ? <div className="ac-card"><p className="ac-muted">{error}</p></div> : !data ? <p className="ac-muted">Loading your account…</p>
            : section === "library" ? <Library data={data} history={history} /> : section === "subscriptions" ? <Subscriptions history={history} /> : section === "payments" ? <Payments user={user} /> : section === "settings" ? <SettingsHub history={history} /> : section === "gifts" ? <Gifts /> : section === "applications" ? <Applications history={history} /> : section === "bills" ? <Bills history={history} /> : <Overview data={data} history={history} />}
        </main>
      </div>
    </div>
  );
}
